"use client";

import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from "react";

export interface FollowUpTaskMessageData {
  subject: string;
  body: string;
  recipients: string[];
  ccRecipients?: string[];
  bccRecipients?: string[];
  fromAccount: string;
  fromAccountLabel?: string;
  selectedClient?: {
    id: string;
    name: string;
    email: string;
  };
}

/**
 * The created `contact_tasks` row, handed back to whoever asked for the
 * follow-up. `unknown` rather than `any` (main-03): the row shape is whatever
 * `useCreateTask` returned, every consumer narrows what it actually reads, and
 * this matches the same callback's existing declaration in
 * `lib/modals/modal-data/tickets-tasks-time.ts`.
 */
export type FollowUpTaskCreatedCallback = (task: unknown) => void;

interface FollowUpTaskContextType {
  messageData: FollowUpTaskMessageData | null;
  setMessageData: (data: FollowUpTaskMessageData | null) => void;
  onTaskCreated?: FollowUpTaskCreatedCallback;
  setOnTaskCreated: (callback: FollowUpTaskCreatedCallback | undefined) => void;
}

const FollowUpTaskContext = createContext<FollowUpTaskContextType | undefined>(undefined);

interface FollowUpTaskProviderProps {
  children: ReactNode;
}

export function FollowUpTaskProvider({ children }: FollowUpTaskProviderProps) {
  const [messageData, setMessageData] = useState<FollowUpTaskMessageData | null>(null);
  const [onTaskCreated, setOnTaskCreatedState] = useState<
    FollowUpTaskCreatedCallback | undefined
  >(undefined);

  /**
   * The stored value here IS a function, and a raw `useState` setter cannot
   * hold one: React treats a function argument as an UPDATER and calls it with
   * the previous state, so `setOnTaskCreated(cb)` used to invoke `cb(previous
   * callback)` and store whatever it returned — usually `undefined`. The
   * callback was therefore never stored, and was called once with the wrong
   * argument on its way to being dropped.
   *
   * Wrapping in `() => callback` makes the updater RETURN the callback, which
   * is the documented way to put a function into `useState`. `useCallback` with
   * an empty dep list keeps the setter's identity stable, which the provider's
   * memoized context value depends on.
   */
  const setOnTaskCreated = useCallback(
    (callback: FollowUpTaskCreatedCallback | undefined): void => {
      setOnTaskCreatedState(() => callback);
    },
    []
  );

  // Debug logging
  React.useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log("🔍 FollowUpTaskContext messageData updated:", messageData);
    }
  }, [messageData]);

  /**
   * Memoized: this value used to be a fresh object literal on every provider
   * render, so every `useFollowUpTaskContext` consumer re-rendered whether or
   * not anything it reads had changed (MEMO_STABLE_PROPS, context edition).
   * Both setters are stable — `setMessageData` is a `useState` setter and
   * `setOnTaskCreated` is `useCallback`-wrapped — so the identity now changes
   * only when the data actually does.
   */
  const contextValue = useMemo<FollowUpTaskContextType>(
    () => ({
      messageData,
      setMessageData,
      onTaskCreated,
      setOnTaskCreated,
    }),
    [messageData, onTaskCreated, setOnTaskCreated]
  );

  return (
    <FollowUpTaskContext.Provider value={contextValue}>{children}</FollowUpTaskContext.Provider>
  );
}

export function useFollowUpTaskContext() {
  const context = useContext(FollowUpTaskContext);
  if (context === undefined) {
    throw new Error("useFollowUpTaskContext must be used within a FollowUpTaskProvider");
  }
  return context;
}
